/*!
 * (c) 2026 Ocean Essentials Limited (HK). All rights reserved.
 * Proprietary code. Licensed, not sold. See LICENSE.md and Services Agreement.
 */

/**
 * Variant Swap 2026 -- shared in-place product swap
 * -------------------------------------------------
 * Exocet flat products (each size/construction/pack combo = a separate
 * product). Switching between siblings would normally be a full-page
 * navigation; this helper swaps the rendered section in place instead,
 * via the Shopify Section Rendering API -- crossfades the gallery, updates
 * the info panel + specs drawers, pushes the URL (no reload, scroll kept).
 *
 * Used by both board-variant-picker.js and pack-variant-picker.js.
 *
 * window.ExocetVariantSwap.swap({
 *   section,        // the .product-hero-2026 element
 *   sectionId,      // section id for ?section_id= and specs drawers
 *   url,            // sibling product url
 *   variant,        // { variantId, price, compare_at_price } for the change event
 *   onAfterSwap,    // optional (doc, variant) hook: caller refreshes its own picker
 * }) -> Promise
 *
 * On fetch failure it falls back to a full navigation to `url`.
 */
(function () {
  "use strict";

  if (window.ExocetVariantSwap) return;

  var anySwapDone = false;

  function fetchSection(productUrl, sectionId, signal) {
    return fetch(productUrl + "?section_id=" + sectionId, { signal: signal })
      .then(function (res) {
        if (!res.ok) throw new Error("HTTP " + res.status);
        return res.text();
      })
      .then(function (text) {
        return new DOMParser().parseFromString(text, "text/html");
      });
  }

  // ------------------------------------------------------------------
  // Gallery crossfade
  //
  // 1. Capture current active image src
  // 2. Swap gallery content with server-rendered HTML
  // 3. Overlay old image on top (covers the unloaded new image)
  // 4. Preload new image
  // 5. Fade out overlay (true crossfade -- no blank flash)
  //
  // Note: gallery content is from Shopify Section Rendering API
  // (same-origin, Liquid-rendered). This is the standard pattern
  // used in Dawn's product-info.js for variant media swaps.
  // ------------------------------------------------------------------

  async function crossfade(gallery, doc) {
    var newGallery = doc.querySelector(".product-gallery-2026");
    if (!newGallery) throw new Error("Gallery not found in response");

    // Capture current main image
    var currentImg = gallery.querySelector(".gallery-main-2026__slide.active img");
    var currentSrc = currentImg ? currentImg.src : null;

    // Replace gallery children with server-rendered content
    while (gallery.firstChild) gallery.removeChild(gallery.firstChild);
    while (newGallery.firstChild) gallery.appendChild(newGallery.firstChild);

    if (!currentSrc) return;

    var mainArea = gallery.querySelector(".gallery-main-2026");
    if (!mainArea) return;

    // Create overlay with old image
    var overlay = document.createElement("img");
    overlay.src = currentSrc;
    overlay.className = "gallery-swap-overlay";
    overlay.alt = "";
    overlay.setAttribute("aria-hidden", "true");
    mainArea.appendChild(overlay);

    // Wait for new main image to load
    await preloadFirstImage(gallery);

    // Trigger crossfade (force layout before adding class)
    void overlay.offsetHeight;
    overlay.classList.add("fade-out");

    // Wait for transition to finish
    await new Promise(function (resolve) {
      overlay.addEventListener("transitionend", resolve, { once: true });
      setTimeout(resolve, 500);
    });

    overlay.remove();
  }

  function preloadFirstImage(container) {
    var img = container.querySelector(".gallery-main-2026__slide.active img");
    if (!img || (img.complete && img.naturalWidth > 0)) return Promise.resolve();

    return new Promise(function (resolve) {
      img.addEventListener("load", resolve, { once: true });
      img.addEventListener("error", resolve, { once: true });
      setTimeout(resolve, 2000);
    });
  }

  // ------------------------------------------------------------------
  // Info panel updates (surgical -- containers stay, only content swaps)
  //
  // Content source: Shopify Section Rendering API (same-origin Liquid).
  // Same pattern as Dawn product-info.js handleSwapProduct().
  // ------------------------------------------------------------------

  function updateInfo(section, doc, variant) {
    // Title
    swapInner(section, doc, ".product-title-2026");

    // Construction badge
    swapInner(section, doc, ".product-meta-2026");

    // Tagline / volume / rating / description body (size-dependent blocks)
    swapInner(section, doc, ".product-tagline-2026");
    swapInner(section, doc, ".product-volume-2026");
    swapInner(section, doc, ".product-rating-2026");
    swapInner(section, doc, ".product-description-2026__body");

    // Picker current-size displays -- server-authoritative so the unit
    // (e.g. "84L", "5.5 m²", "70 cm") is preserved on a size swap. The picker
    // buttons' selected state is set optimistically by the picker itself.
    swapInner(section, doc, "[data-bvp-volume-value]");
    swapInner(section, doc, "[data-bvp-length-value]");
    swapInner(section, doc, "[data-bvp-width-value]");
    swapInner(section, doc, "[data-bvp-simple-value]");

    // Price
    swapInner(section, doc, "[data-price]");

    // Stock status
    swapInner(section, doc, "[data-stock-status]");

    // Buy button (sold out ↔ in stock markup differs after swap)
    swapInner(section, doc, "[data-buy-buttons]");
    // Re-hydrate dynamic checkout (Buy it now / Shop Pay) -- the swapped-in
    // payment_button markup is inert until Shopify's SDK re-inits it.
    if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();

    // Recommended accessory upsell cards (one block per upsell in theme editor)
    // Skip the JS-injected pump fallback card: it never exists in the fetched
    // doc, so including it would misalign the index mapping below.
    var oldAccessoryBlocks = section.querySelectorAll(".rec-accessory-block:not([data-pump-fallback])");
    var newAccessoryBlocks = doc.querySelectorAll(".rec-accessory-block");
    oldAccessoryBlocks.forEach(function (accessoryBlock, index) {
      var newAccessoryBlock = newAccessoryBlocks[index];
      if (newAccessoryBlock) {
        accessoryBlock.innerHTML = newAccessoryBlock.innerHTML;
        accessoryBlock.hidden = newAccessoryBlock.hidden;
      } else {
        accessoryBlock.hidden = true;
      }
    });

    // Breadcrumb
    var newCrumb = doc.querySelector(".product-breadcrumb-2026 [aria-current]");
    var crumb = section.querySelector(".product-breadcrumb-2026 [aria-current]");
    if (newCrumb && crumb) crumb.textContent = newCrumb.textContent;

    // Form variant ID + product context -- server-authoritative from the
    // fetched doc (works for every mode, incl. simple/boardbag whose variant
    // objects carry no variantId).
    var input = section.querySelector('input[name="id"]');
    var newInput = doc.querySelector('input[name="id"]');
    if (input && newInput) input.value = newInput.value;

    var productIdInput = section.querySelector('input[name="product-id"]');
    var newProductIdInput = doc.querySelector('input[name="product-id"]');
    if (productIdInput && newProductIdInput) {
      productIdInput.value = newProductIdInput.value;
    }
  }

  // Refresh the specs drawer + rig drawer bodies from the fetched document.
  // The drawers live OUTSIDE .product-hero-2026 but inside the same
  // main-product section, so the fetched doc carries the fully server-rendered
  // new body (correct current specs + is-current row). We swap the whole body,
  // then let the drawer re-bind its body-local listeners. Persistent listeners
  // survive (they sit on the header/overlay/drawer/trigger).
  function updateSpecsDrawers(doc, sectionId) {
    swapDrawerBody(doc, "SpecsDrawer-" + sectionId, ".specs-drawer-2026__body", window.ExocetSpecsDrawer, sectionId);
    swapDrawerBody(
      doc,
      "RigSpecsDrawer-" + sectionId,
      ".rig-specs-drawer-2026__body",
      window.ExocetRigSpecsDrawer,
      sectionId,
    );
  }

  function dispatchVariantChange(variant) {
    if (!variant || typeof variant.price !== "number") return;
    var detail = {
      variant: {
        id: variant.variantId,
        price: variant.price,
        compare_at_price:
          variant.compare_at_price != null && variant.compare_at_price > 0 ? variant.compare_at_price : null,
      },
    };
    document.dispatchEvent(new CustomEvent("variant:change", { detail: detail }));
    document.dispatchEvent(new CustomEvent("product:variant-change", { detail: detail }));
  }

  function swapInner(section, doc, selector) {
    var src = doc.querySelector(selector);
    var dest = section.querySelector(selector);
    if (src && dest) {
      while (dest.firstChild) dest.removeChild(dest.firstChild);
      while (src.firstChild) dest.appendChild(src.firstChild);
    }
  }

  // Replace a drawer's body content from the fetched doc, then re-bind the
  // drawer's body-local listeners. Dest lookup is global (document) because the
  // drawer sits outside .product-hero-2026. No-ops if the drawer is absent for
  // this product (e.g. boards have no rig drawer).
  function swapDrawerBody(doc, drawerId, bodySelector, ns, sectionId) {
    var srcDrawer = doc.getElementById(drawerId);
    var destDrawer = document.getElementById(drawerId);
    if (!srcDrawer || !destDrawer) return;
    var src = srcDrawer.querySelector(bodySelector);
    var dest = destDrawer.querySelector(bodySelector);
    if (!src || !dest) return;
    // Move the server-rendered nodes in (same pattern as swapInner): clears any
    // listeners the old body had, which is why bindBody re-attaches them below.
    while (dest.firstChild) dest.removeChild(dest.firstChild);
    while (src.firstChild) dest.appendChild(src.firstChild);
    if (ns && typeof ns.bindBody === "function") ns.bindBody(sectionId);
  }

  // ------------------------------------------------------------------
  // Public swap. Aborts any in-flight swap on the same section, fetches the
  // sibling section, crossfades + updates info, pushes the URL, then calls
  // onAfterSwap(doc, variant) so the caller can refresh its own picker state.
  // ------------------------------------------------------------------

  async function swap(opts) {
    var section = opts.section;
    var sectionId = opts.sectionId;
    var url = opts.url;
    var variant = opts.variant;
    var onAfterSwap = opts.onAfterSwap;

    if (!section || !url) return;

    // Abort any in-flight swap on this section, then register ours.
    if (section.__exocetSwapAbort) {
      try {
        section.__exocetSwapAbort.abort();
      } catch (e) {}
    }
    var controller = new AbortController();
    section.__exocetSwapAbort = controller;

    var gallery = section.querySelector(".product-gallery-2026");

    // Delayed loading state (only visible if fetch > 300ms)
    var loadingTimer = setTimeout(function () {
      if (gallery) gallery.classList.add("is-loading");
    }, 300);

    try {
      var doc = await fetchSection(url, sectionId, controller.signal);

      clearTimeout(loadingTimer);
      if (gallery) gallery.classList.remove("is-loading");

      if (gallery) await crossfade(gallery, doc);

      // A newer selection can abort this swap after its fetch has completed,
      // while the gallery crossfade is still waiting on image decode. Never let
      // that stale response overwrite the latest product URL, info or form ID.
      if (controller.signal.aborted || section.__exocetSwapAbort !== controller) return;

      updateInfo(section, doc, variant);
      updateSpecsDrawers(doc, sectionId);
      dispatchVariantChange(variant);

      history.pushState({ bvp: true }, "", url);
      anySwapDone = true;

      if (typeof onAfterSwap === "function") onAfterSwap(doc, variant);

      section.dispatchEvent(new CustomEvent("gallery:refresh"));
    } catch (err) {
      clearTimeout(loadingTimer);
      if (gallery) gallery.classList.remove("is-loading");

      if (err && err.name === "AbortError") return;
      console.error("[VariantSwap] Swap failed, navigating:", url, err);
      window.location.href = url;
    } finally {
      if (section.__exocetSwapAbort === controller) section.__exocetSwapAbort = null;
    }
  }

  // Browser back/forward after a pushState swap: the DOM was surgically
  // mutated, so reload to render the prior product cleanly.
  window.addEventListener("popstate", function () {
    if (anySwapDone) window.location.reload();
  });

  window.ExocetVariantSwap = { swap: swap };
})();
